import { Fragment, useState, useEffect, useCallback } from "react";
import * as yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import makeStyles from "styles/muiStylesCompat/makeStyles";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import ButtonDefault from "components/Buttons/ButtonDefault";
import ButtonPrimary from "components/Buttons/ButtonPrimary";
import ButtonDelete from "components/Buttons/ButtonDelete";

import { getVehicle, saveVehicle, deleteVehicle } from "services/http/vehicles";
import { uploadImageFile } from "services/http/upload";
import SpinnerCentered from "~/components/Loader/SpinnerCentered";
import AlertDialog from "components/AlertDialog";
import { validateCompany } from "services/companies";

import VehicleForm from "components/VehicleForm";

const useStyles = makeStyles((_theme) => ({
  content: { padding: 48 },
  title: { paddingBottom: 32 },
}));

// helper for yup transform function
function emptyStringToNull(value, originalValue) {
  if (typeof originalValue === "string" && originalValue === "") {
    return null;
  }
  return value;
}

const validationSchema = yup.object().shape({
  license_number: yup.string().required().max(10) /*
  carbon_dioxide_emission: yup
      .number()
      .integer()
      .max(5), */,
  carbon_dioxide_emission: yup
    .number()
    .integer()
    .max(99999)
    .transform(emptyStringToNull)
    .nullable(),
});

const VehicleEdit = (props) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const classes = useStyles();

  const navigate = useNavigate();
  const { vehicleId } = useParams();
  const { myCompany, getMyCompany, myUser } = props;

  const [values, setValues] = useState({
    animals: false,
    rollator: false,
    wheelchair: false,
    taped: false,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);

  const handleValidateCompany = useCallback(() => {
    if (validateCompany(myCompany) === false) {
      setAlertOpen(true);
    }
  }, [myCompany]);

  const handleGetVehicle = useCallback(() => {
    setLoading(true);
    const request = { data: { id: vehicleId } };

    getVehicle(request).then(
      (response) => {
        setValues(response);
        setLoading(false);
      },
      (_errors) => {
        setLoading(false);
      },
    );
  }, [vehicleId]);

  useEffect(() => {
    handleValidateCompany();
  }, [handleValidateCompany]);

  useEffect(() => {
    if (vehicleId && vehicleId >= 0) {
      handleGetVehicle();
    }
  }, [vehicleId, handleGetVehicle]);

  const updateValues = (updated_values) => {
    if (updated_values == null) {
      return;
    }

    setValues((prev) => ({ ...prev, ...updated_values }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setSaving(true);

    const request = {
      data: {
        ...values,
        price_list:
          values?.price_list != null && values?.price_list > 0
            ? values?.price_list
            : null,
      },
    };
    delete request.data.image;

    if (myUser?.status != 100 || values?.id) {
      // Only admin can change vehicle license number
      delete request.data.license_number;
    }

    saveVehicle(request).then(
      (response) => {
        setValues(response);
        setSaving(false);
        setErrors({});
        enqueueSnackbar("Tallennettu", {
          variant: "success",
          anchorOrigin: { vertical: "bottom", horizontal: "center" },
        });

        if (values.imageFile) {
          uploadImageFile(
            "/cars/" + response.id + "/",
            "image",
            values.imageFile,
          ).then(
            (_response) => {
              getMyCompany();
              navigate("/dashboard/vehicles/list");
            },
            (_errors) => {
              enqueueSnackbar("Ajoneuvon kuvan tallennus epäonnistui", {
                variant: "success",
                anchorOrigin: { vertical: "bottom", horizontal: "center" },
              });
            },
          );
        } else {
          getMyCompany();
          navigate("/dashboard/vehicles/list");
        }
      },
      (errors) => {
        setSaving(false);
        enqueueSnackbar("Tallennus epäonnistui", {
          variant: "error",
          anchorOrigin: { vertical: "bottom", horizontal: "center" },
        });
        setErrors(errors?.data);
      },
    );
  };

  const handleErrors = (_errors) => {};

  const handleDelete = (confirmed) => {
    if (confirmed == false) {
      setConfirmOpen(true);
    } else {
      deleteVehicle(values?.id).then(
        (_response) => {
          enqueueSnackbar("Poistettu", {
            variant: "success",
            anchorOrigin: { vertical: "bottom", horizontal: "center" },
          });
          navigate("/dashboard/vehicles/list");
        },
        (_error) => {
          enqueueSnackbar("Ei voi poistaa. Käytössä.", {
            variant: "error",
            anchorOrigin: { vertical: "bottom", horizontal: "center" },
          });
        },
      );
    }
  };

  const validateForm = () => {
    validationSchema
      .validate(values, { abortEarly: true })
      .then(() => {
        // GOOD
        setErrors({});
      })
      .catch((err) => {
        // INVALID
        setErrors({ [err.path]: t(err?.message), inValid: true });
      });
  };

  return (
    <Paper className={classes.content} elevation={0} square>
      <Grid
        container
        className={classes.title}
        spacing={3}
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center" }}
      >
        <Grid size={12}>
          <Typography variant="h4">{t("Lisää uusi auto")}</Typography>
        </Grid>
      </Grid>

      <form onSubmit={handleSubmit} onError={handleErrors}>
        <Box sx={{ mb: "32px" }}>
          {loading ? (
            <SpinnerCentered />
          ) : (
            <VehicleForm
              values={values}
              updateValues={updateValues}
              errors={errors}
              validateForm={validateForm}
            />
          )}
        </Box>

        {errors && errors.inValid ? (
          <Fragment>
            <Typography variant="subtitle2" color="error" gutterBottom>
              {t("Lomakkeella on virheitä")}
            </Typography>
            <Box>
              {Object.keys(errors).map((key) => (
                <Box key={key}>
                  <Typography variant="body" color="error" gutterBottom>
                    {errors[key]}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Fragment>
        ) : null}

        <Grid size={{ xs: 12 }}>
          <Grid
            container
            direction="row"
            sx={{ justifyContent: "space-between", alignItems: "flex-end" }}
          >
            <Grid size={12}>
              <Grid
                container
                direction="row"
                spacing={1}
                sx={{ justifyContent: "flex-start", alignItems: "center" }}
              >
                <Grid size={12}>
                  <ButtonDefault onClick={() => navigate("/dashboard/vehicles/list")}>
                    {t("Peruuta")}
                  </ButtonDefault>
                </Grid>
                {myUser?.status == 100 && values?.id && values?.id >= 0 ? (
                  <Grid size={12}>
                    <ButtonDelete onClick={() => handleDelete(false)}>
                      {t("Poista auto")}
                    </ButtonDelete>
                  </Grid>
                ) : null}
              </Grid>
            </Grid>
            {myUser?.status == 100 ? (
              <Grid size={12}>
                {values?.id && values?.id >= 0 ? (
                  <ButtonPrimary
                    type="submit"
                    loading={saving}
                    disabled={errors?.inValid}
                  >
                    {t("Tallenna")}
                  </ButtonPrimary>
                ) : (
                  <ButtonPrimary
                    type="submit"
                    loading={saving}
                    disabled={errors?.inValid}
                  >
                    {t("Lisää auto")}
                  </ButtonPrimary>
                )}
              </Grid>
            ) : null}
          </Grid>
        </Grid>
      </form>

      <AlertDialog
        titleText={t("Huom!")}
        contentText={t(
          "Sinun täytyy lisätä yrityksesi maksutiedot ja hinnasto, jotta voit lisätä kuljettajia ja autoja.",
        )}
        open={alertOpen}
        setOpen={setAlertOpen}
        showCancel={false}
        confirmText={t("OK")}
        handleConfirm={() => navigate("/dashboard/start")}
        isModal={true}
      />

      <AlertDialog
        titleText={t("Vahvista poisto")}
        contentText={t("Haluatko varmasti poistaa auton?")}
        open={confirmOpen}
        setOpen={setConfirmOpen}
        cancelText={t("Peruuta")}
        confirmText={t("Poista")}
        handleConfirm={() => handleDelete(true)}
      />
    </Paper>
  );
};

export default VehicleEdit;
